/**
 * Pick a readable foreground color (black or white) for a given background.
 *
 * Uses the WCAG relative-luminance formula and returns whichever foreground
 * has the higher contrast ratio. Accepts 3- or 6-digit hex strings. Falls
 * back to white for unparseable inputs (e.g. CSS `var()` strings — the
 * project's brand-default colors are all saturated and white-safe).
 */

const parseHex = (hex: string): [number, number, number] | null => {
  const m = hex.trim().match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const relativeLuminance = (r: number, g: number, b: number): number => {
  const toLinear = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const contrastRatio = (lum1: number, lum2: number): number => {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
};

export const getContrastText = (background: string): string => {
  const rgb = parseHex(background);
  if (!rgb) return '#ffffff';
  const bgLum = relativeLuminance(rgb[0], rgb[1], rgb[2]);
  return contrastRatio(bgLum, 1) >= contrastRatio(bgLum, 0) ? '#ffffff' : '#1a1a1a';
};
